import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  ChevronLeft, TrendingUp, Trophy, Calendar, Flame, ArrowRight
} from "lucide-react";
import ProgressSection from "./ProgressSection";
import BottomNav from "../../components/ui/BottomNav";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VoortgangPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [voortgang, setVoortgang] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.token) return;
    const h = { Authorization: `Bearer ${user.token}` };
    Promise.all([
      fetch(`${API}/api/sporter/voortgang`, { headers: h }),
      fetch(`${API}/api/workout/progress-dashboard`, { headers: h }),
    ]).then(async ([r1, r2]) => {
      if (r1.ok) setVoortgang(await r1.json());
      if (r2.ok) setProgressData(await r2.json());
    }).finally(() => setLoading(false));
  }, [user?.token]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="max-w-2xl mx-auto px-4 pt-5 pb-28">
        <button onClick={() => navigate("/account")}
          className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm font-semibold mb-6 transition-colors">
          <ChevronLeft size={16} /> Profiel
        </button>
        <h1 className="text-white font-black text-2xl mb-6">Voortgang</h1>

        {/* Quick stats */}
        {!loading && voortgang && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { icon: <Calendar size={14} />, val: voortgang.per_week?.at(-1)?.count ?? 0, label: "Deze week" },
              { icon: <Flame size={14} />,    val: voortgang.streak_weken ?? 0,             label: "Streak" },
              { icon: <Trophy size={14} />,   val: voortgang.totaal_alltime ?? 0,           label: "Totaal" },
            ].map(({ icon, val, label }) => (
              <div key={label} className="bg-[#111] border border-white/8 rounded-2xl p-4 text-center">
                <div className="text-[#C9A84C] flex justify-center mb-1.5">{icon}</div>
                <p className="text-2xl font-black text-white tabular-nums">{val}</p>
                <p className="text-zinc-600 text-xs mt-1">{label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Top PRs */}
        {!loading && progressData?.oefeningen?.length > 0 && (
          <div className="account-card mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Trophy size={14} className="text-[#C9A84C]" />
                <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Persoonlijke Records</p>
              </div>
              <button onClick={() => navigate("/progress")}
                className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors">
                Volledig dashboard →
              </button>
            </div>
            <div className="space-y-2.5">
              {progressData.oefeningen.slice(0, 5).map((oef, i) => (
                <div key={i} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black text-black shrink-0"
                      style={{ background: i === 0 ? "#FFD700" : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : "#333" }}>
                      {i + 1}
                    </span>
                    <p className="text-zinc-300 text-sm truncate">{oef.naam}</p>
                  </div>
                  <span className="text-[#C9A84C] font-black text-sm tabular-nums shrink-0">{oef.beste_1rm} kg</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Progress dashboard knop */}
        <button onClick={() => navigate("/progress")}
          className="w-full flex items-center gap-3 px-5 py-4 rounded-2xl border border-white/8 hover:border-[#C9A84C]/30 transition-all group mb-6"
          style={{ background: "#111" }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "rgba(201,168,76,0.1)" }}>
            <TrendingUp size={18} style={{ color: "#C9A84C" }} />
          </div>
          <div className="flex-1 text-left">
            <p className="text-white font-bold text-sm">Volledig Progress Dashboard</p>
            <p className="text-zinc-500 text-xs mt-0.5">Kracht & consistentie over tijd</p>
          </div>
          <ArrowRight size={15} className="text-zinc-600 group-hover:text-[#C9A84C] transition-colors" />
        </button>

        {/* ProgressSection */}
        <ProgressSection user={user} />
      </div>
      <BottomNav />
    </div>
  );
}
